'use client';

import { c as _c } from "react/compiler-runtime";
import { type PinnedFactiii } from '@shared/all/domains/factiiis';
import useNavigator from '@shared/utils/hooks/useNavigator';
import { type ReactNode, useState } from 'react';
import Header from '../../components/common/Header';
import PostRenderOptions from '../../components/posts/PostRenderOptions';
import { LAYOUT_GAP, OFFLINE_BANNER_HEIGHT } from '../../constants/layout';
import { useHeaderHeight } from '../../hooks/useHeaderHeight';
import useIsElectron from '../../hooks/useIsElectron';
import EmbedLayout from './EmbedLayout';
import MobileSidebarModal from './MobileSidebarModal';
import MobileSidebarTriggers from './MobileSidebarTriggers';
import SidebarSection from './SidebarSection';
interface SidebarItem {
  icon: React.ReactNode;
  text: string;
  component?: React.ReactNode;
  dataId?: string;
  action?: React.ReactNode;
}
interface SidebarProps {
  items: SidebarItem[];
}
type Props = {
  fullWidth?: boolean;
  topBanner?: ReactNode;
  children: ReactNode;
  leftSidebar?: SidebarProps;
  rightSidebar?: SidebarProps;
  hideHeader?: boolean;
  showPostRenderOptions?: boolean;
  disableNewPost?: boolean;
  spaceSlug?: string;
  pinnedFactiii?: PinnedFactiii;
  isEmbed?: boolean;
};
const AppLayout = t0 => {
  const $ = _c(61);
  const {
    children,
    topBanner: t1,
    leftSidebar: t2,
    rightSidebar: t3,
    fullWidth: t4,
    hideHeader: t5,
    showPostRenderOptions: t6,
    disableNewPost,
    spaceSlug,
    pinnedFactiii,
    isEmbed: t7
  } = t0;
  const topBanner = t1 === undefined ? null : t1;
  const leftSidebar = t2 === undefined ? undefined : t2;
  const rightSidebar = t3 === undefined ? undefined : t3;
  const fullWidth = t4 === undefined ? false : t4;
  const hideHeader = t5 === undefined ? false : t5;
  const showPostRenderOptions = t6 === undefined ? false : t6;
  const isEmbed = t7 === undefined ? false : t7;
  const {
    navigatorState
  } = useNavigator();
  const isElectron = useIsElectron();
  const [isHeaderVisible, setIsHeaderVisible] = useState(!hideHeader);
  const [activeComponent, setActiveComponent] = useState();
  const headerHeight = useHeaderHeight();
  if (isEmbed) {
    let t8;
    if ($[0] !== children) {
      t8 = <EmbedLayout>{children}</EmbedLayout>;
      $[0] = children;
      $[1] = t8;
    } else {
      t8 = $[1];
    }
    return t8;
  }
  const headerVisible = isHeaderVisible || isElectron;
  const topOffset = (headerVisible ? headerHeight : 0) + (navigatorState.isOnline === false ? OFFLINE_BANNER_HEIGHT : 0);
  const stickyTopOffset = headerVisible ? topOffset : LAYOUT_GAP;
  let t8;
  if ($[2] !== fullWidth || $[3] !== hideHeader || $[4] !== isHeaderVisible) {
    t8 = !hideHeader && <Header isHeaderVisible={isHeaderVisible} setIsHeaderVisible={setIsHeaderVisible} disableAutoHide={fullWidth} />;
    $[2] = fullWidth;
    $[3] = hideHeader;
    $[4] = isHeaderVisible;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  let t9;
  if ($[6] !== headerHeight || $[7] !== isHeaderVisible || $[8] !== navigatorState.isOnline) {
    t9 = navigatorState.isOnline === false && <div className="sticky z-50 w-full border-y border-ds-border-standard bg-background-secondary transition-all duration-300" style={{
      top: isHeaderVisible ? headerHeight : 0
    }}><div className="flex w-full items-center justify-center px-4 py-3"><div className="flex items-center space-x-2 text-textPrimary"><svg className="size-5 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.876c1.214 0 2.194-1.03 1.808-2.209L16.66 7.545c-.31-.847-1.125-1.445-2.007-1.445H9.347c-.882 0-1.697.598-2.007 1.445L2.254 16.791C1.868 17.97 2.848 19 4.062 19z" /></svg><span className="text-sm font-emphasis">You're offline. Some features may be limited.</span></div></div></div>;
    $[6] = headerHeight;
    $[7] = isHeaderVisible;
    $[8] = navigatorState.isOnline;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const t10 = `flex flex-col ${fullWidth ? "h-screen" : ""}`;
  const t11 = `grid w-full grid-cols-1 transition-all duration-300 ease-in-out ${fullWidth ? "min-h-0 flex-1" : ""} ${leftSidebar ? "gap-3 md:grid-cols-8" : ""} ${rightSidebar ? "lg:grid-cols-12" : ""}`;
  let t12;
  if ($[10] !== topBanner || $[11] !== topOffset) {
    t12 = !topBanner ? {
      marginTop: `${topOffset}px`
    } : undefined;
    $[10] = topBanner;
    $[11] = topOffset;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] !== fullWidth || $[14] !== leftSidebar || $[15] !== rightSidebar || $[16] !== stickyTopOffset) {
    t13 = !fullWidth && leftSidebar && <SidebarSection items={leftSidebar.items} position="left" stickyTopOffset={stickyTopOffset} hasOppositeSidebar={Boolean(rightSidebar)} />;
    $[13] = fullWidth;
    $[14] = leftSidebar;
    $[15] = rightSidebar;
    $[16] = stickyTopOffset;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  const t14 = `col-span-12 ${fullWidth ? "flex min-h-0 flex-col" : ""} ${leftSidebar && !rightSidebar ? "md:col-span-8 laptop:col-span-5 lg:col-span-4" : leftSidebar ? "laptop:col-span-5" : ""} ${rightSidebar ? "lg:col-span-6" : ""}`;
  const t15 = `${fullWidth ? "flex min-h-0 flex-1 flex-col" : "mx-auto laptop:max-w-screen-sm lg:w-full"} w-full`;
  const t16 = `${fullWidth ? "flex min-h-0 flex-1 flex-col" : "mx-auto laptop:max-w-screen-sm lg:max-w-screen-sm"} w-full`;
  let t17;
  if ($[18] !== leftSidebar || $[19] !== rightSidebar || $[20] !== topBanner) {
    t17 = topBanner && <div className="mb-4">{topBanner}<MobileSidebarTriggers leftSidebar={leftSidebar} rightSidebar={rightSidebar} onItemClick={setActiveComponent} /></div>;
    $[18] = leftSidebar;
    $[19] = rightSidebar;
    $[20] = topBanner;
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  let t18;
  if ($[22] !== disableNewPost || $[23] !== headerVisible || $[24] !== pinnedFactiii || $[25] !== showPostRenderOptions || $[26] !== spaceSlug || $[27] !== stickyTopOffset) {
    t18 = showPostRenderOptions && <div className={`sticky z-40 mb-4 bg-background-primary transition-all duration-300 ease-in-out ${!headerVisible ? "pointer-events-none -translate-y-full opacity-0" : ""}`} style={{
      top: `${stickyTopOffset}px`
    }}><PostRenderOptions pinnedFactiii={pinnedFactiii} disableNewPost={disableNewPost} spaceSlug={spaceSlug} /></div>;
    $[22] = disableNewPost;
    $[23] = headerVisible;
    $[24] = pinnedFactiii;
    $[25] = showPostRenderOptions;
    $[26] = spaceSlug;
    $[27] = stickyTopOffset;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== children || $[30] !== t16 || $[31] !== t17 || $[32] !== t18) {
    t19 = <div className={t16}>{t17}{t18}{children}</div>;
    $[29] = children;
    $[30] = t16;
    $[31] = t17;
    $[32] = t18;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== t15 || $[35] !== t19) {
    t20 = <div className={t15}>{t19}</div>;
    $[34] = t15;
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== t14 || $[38] !== t20) {
    t21 = <div className={t14}>{t20}</div>;
    $[37] = t14;
    $[38] = t20;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== fullWidth || $[41] !== leftSidebar || $[42] !== rightSidebar || $[43] !== stickyTopOffset) {
    t22 = !fullWidth && rightSidebar && <SidebarSection items={rightSidebar.items} position="right" stickyTopOffset={stickyTopOffset} hasOppositeSidebar={Boolean(leftSidebar)} />;
    $[40] = fullWidth;
    $[41] = leftSidebar;
    $[42] = rightSidebar;
    $[43] = stickyTopOffset;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== t11 || $[46] !== t12 || $[47] !== t13 || $[48] !== t21 || $[49] !== t22) {
    t23 = <div className={t11} style={t12}>{t13}{t21}{t22}</div>;
    $[45] = t11;
    $[46] = t12;
    $[47] = t13;
    $[48] = t21;
    $[49] = t22;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== t10 || $[52] !== t23) {
    t24 = <div className={t10}>{t23}</div>;
    $[51] = t10;
    $[52] = t23;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== activeComponent) {
    t25 = activeComponent && <MobileSidebarModal activeComponent={activeComponent} onClose={() => setActiveComponent(undefined)} />;
    $[54] = activeComponent;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== t24 || $[57] !== t25 || $[58] !== t8 || $[59] !== t9) {
    t26 = <div className="size-full" aria-label="Main content">{t8}{t9}{t24}{t25}</div>;
    $[56] = t24;
    $[57] = t25;
    $[58] = t8;
    $[59] = t9;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  return t26;
};
export default AppLayout;